/**
 * Окружение, в котором работает сборка, с точки зрения аналитики. Совпадает с матрицей
 * `shared/hooks/use-is-test.ts`, но выражено функцией, а не хуком: ключ выбирается в
 * `initAmplitude()` вне React-дерева.
 *
 * | env       | NODE_ENV      | REACT_APP_ENVIRONMENT | результат      |
 * |-----------|---------------|-----------------------|----------------|
 * | локальный | 'development' | —                     | 'local'        |
 * | TEST      | 'production'  | 'development'         | 'test'         |
 * | PROD      | 'production'  | 'production'          | 'production'   |
 */
export type AnalyticsEnvironment = 'local' | 'test' | 'production';

export function resolveAnalyticsEnvironment(): AnalyticsEnvironment {
  // `craco build` выставляет NODE_ENV='production' в любой сборке, поэтому 'development' здесь —
  // это ровно дев-сервер, а не «непрод». Проверяется первым: у локального запуска
  // REACT_APP_ENVIRONMENT тоже 'development' (`package.json`, скрипты `start-dev*`).
  if (process.env.NODE_ENV === 'development') {
    return 'local';
  }

  return process.env.REACT_APP_ENVIRONMENT === 'production' ? 'production' : 'test';
}

/**
 * Ключ проекта Amplitude под текущее окружение. Три отдельные переменные, а не одна:
 * `REACT_APP_*` инлайнится на этапе сборки, и с одной переменной локальный запуск,
 * TEST и PROD неизбежно писали бы в один проект — события разработки смешивались бы с
 * продуктовыми, а Session Replay записывал бы дев-сессии как реальные.
 *
 * Fallback-а на общий ключ намеренно нет: он бы вернул ровно ту склейку, от которой
 * разделение и заводится. Пропущенная переменная — это отсутствие аналитики и `console.warn`
 * в `initAmplitude()`, а не тихая запись в чужой проект.
 */
export function resolveAmplitudeApiKey(environment = resolveAnalyticsEnvironment()): string | undefined {
  switch (environment) {
    case 'local':
      return process.env.REACT_APP_AMPLITUDE_API_KEY_LOCAL;
    case 'test':
      return process.env.REACT_APP_AMPLITUDE_API_KEY_TEST;
    case 'production':
      return process.env.REACT_APP_AMPLITUDE_API_KEY_PROD;
    default:
      return undefined;
  }
}
